import { AxiosHeaders } from 'axios';
import { MARKET_ACTION_TIMEOUT_MS, MARKET_READ_TIMEOUT_MS } from '~/features/market/requestPolicy';
import { withRequestDeadline } from '~/utils/requestDeadline';
import { deleteData, getData, postData } from '~/utils/service';

import { ResponseUnmaskStateSchema } from './responseUnmask.schemas';
export { ResponseUnmaskStateSchema } from './responseUnmask.schemas';
export type { ResponseUnmaskState } from './responseUnmask.schemas';
export type UnmaskAction = 'request' | 'approve' | 'reject' | 'withdraw';

export const getResponseUnmaskState = (responseId: number, signal?: AbortSignal) =>
  withRequestDeadline(
    async (requestSignal) => {
      const state = ResponseUnmaskStateSchema.parse(
        await getData('hlxJobResponseUnmaskState', {
          pathParams: { responseId: String(responseId) },
          signal: requestSignal,
        })
      );
      if (state.responseId !== responseId) throw new Error('Consent response scope changed.');
      return state;
    },
    MARKET_READ_TIMEOUT_MS,
    signal
  );

/** Requesting and approving a disclosure both carry the agreement acceptance. */
const needsTerms = (action: UnmaskAction) => action === 'request' || action === 'approve';

/**
 * Answer only the request displayed to the user; never obtain a replacement token here.
 *
 * [MCV-1755] `termsVersion` is the version of the Platform Participation Agreement the
 * user just accepted in the agreement dialog. A request or approval without it is
 * refused here rather than sent, so no caller can disclose identities without the
 * dialog having been shown.
 */
export const performUnmaskAction = (
  responseId: number,
  action: UnmaskAction,
  requestToken: string | null,
  signal?: AbortSignal,
  termsVersion?: string | null
): Promise<void> =>
  withRequestDeadline(
    async (requestSignal) => {
      if (action !== 'request' && !requestToken?.match(/^[a-f0-9]{64}$/)) {
        throw new Error('Refresh the unmask request before answering it.');
      }
      if (needsTerms(action) && !termsVersion) {
        throw new Error('Accept the Platform Participation Agreement first.');
      }
      const options = {
        pathParams: { responseId: String(responseId) },
        signal: requestSignal,
        headers:
          action === 'request' ? undefined : new AxiosHeaders({ 'If-Match': `"${requestToken}"` }),
      };
      if (action === 'withdraw') {
        await deleteData('hlxJobResponseUnmaskWithdraw', options);
      } else {
        const routes = {
          request: 'hlxJobResponseUnmaskRequest',
          approve: 'hlxJobResponseUnmaskApprove',
          reject: 'hlxJobResponseUnmaskReject',
        } as const;
        await postData(routes[action], {
          ...options,
          data: needsTerms(action) ? { termsAccepted: true, termsVersion } : undefined,
        });
      }
    },
    MARKET_ACTION_TIMEOUT_MS,
    signal
  );
